import { ToastContext } from "context/ToastContext";
import { confirmDialog } from "primereact/confirmdialog";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import type {
  IRuleGroupUpdateRequest,
  IRuleGroupUpdateResponse,
} from "types/AdjustmentRules/AdjustmentRules";

import { omitScopeFields } from "../helpers/mapFormToRequestBody";
import {
  buildRuleGroupUpdateRequest,
  extractServerErrorMessage,
  payerCountLabel,
  plural,
  providerCountLabel,
  ruleCountLabel,
  summarizePairs,
} from "../helpers/ruleBucket";
import { useCodeListFamily, useRuleBucket } from "./useRuleBucket";

type TGroupUpdateTrigger = (request: IRuleGroupUpdateRequest<any>) => {
  unwrap: () => Promise<IRuleGroupUpdateResponse<unknown>>;
};

/** The lines of the confirmation every group save shows, with the real counts. */
export function groupSaveConfirmLines({
  updated,
  addedCount,
  removedCount,
  familyCount,
  rename = null,
}: {
  updated: { providerId: string; payerId: number | null }[];
  addedCount: number;
  removedCount: number;
  familyCount: number;
  /** Set only when the user edited the name: the new name and how many members it renames. */
  rename?: { name: string; count: number } | null;
}): string[] {
  const summary = summarizePairs(updated);
  const lines = [
    `Update ${ruleCountLabel(summary.rules)} across ${providerCountLabel(
      summary.providers
    )} and ${payerCountLabel(summary.payers)}?`,
  ];
  if (rename?.count) {
    lines.push(`Renames ${ruleCountLabel(rename.count)} to "${rename.name}".`);
  }
  if (addedCount) lines.push(`Adds ${ruleCountLabel(addedCount)}.`);
  if (removedCount) {
    lines.push(
      `Removes ${ruleCountLabel(
        removedCount
      )} (their attachments and history are deleted).`
    );
  }
  if (familyCount) {
    lines.push(
      `Also updates the code list on ${plural(familyCount, "other rule")}.`
    );
  }
  return lines;
}

/**
 * The group request's `data`: the single-row body without its per-row fields, and without
 * `groupName` unless the user edited the name — the field opens on the scope's display name, and a
 * save that leaves it alone keeps every member's own name.
 */
export function groupSaveData(
  singleRowBody: Record<string, unknown>,
  scopeDisplayName: string
): Record<string, unknown> {
  const data: Record<string, unknown> = omitScopeFields(singleRowBody);
  const name = String(data.groupName ?? "").trim();
  if (name === scopeDisplayName.trim()) delete data.groupName;
  return data;
}

/**
 * CLE-2842 — saves a bucket-mode edit through the type's `…Group` mutation: one atomic request
 * that deletes the removed members, updates every remaining member in scope, creates the added
 * pairs and, when asked, rewrites the code list of every other rule that uses it. It always asks
 * first, with the real counts, and holds a one-save-at-a-time lock from that question until the
 * request resolves (a cancelled or closed question releases it).
 *
 * Returns null when the page is not in bucket mode or the scope is "Only this payer" — the caller
 * then saves the single row exactly as before.
 */
export function useSubmitRuleGroup(trigger: TGroupUpdateTrigger) {
  const bucket = useRuleBucket();
  const family = useCodeListFamily();
  const navigate = useNavigate();
  const { current: toastElement } = useContext(ToastContext);

  if (!bucket || bucket.scope === "PAYER") return null;
  const { scope } = bucket;

  const save = async (
    request: IRuleGroupUpdateRequest<unknown>,
    ruleCount: number
  ) => {
    // A large save takes many seconds: block the page until it resolves (see RuleBucketProvider).
    bucket.markSaving(ruleCount);
    try {
      const response = await trigger(request).unwrap();
      const savedCount = Array.isArray(response?.updated)
        ? response.updated.length + (response.created?.length ?? 0)
        : bucket.activePairs.length;
      const familyUpdated = response?.familyUpdated?.length ?? 0;
      toastElement?.show({
        severity: "success",
        summary: `Rule updated for ${ruleCountLabel(savedCount)}${
          familyUpdated
            ? `, code list updated on ${plural(familyUpdated, "other")}`
            : ""
        }`,
      });
      // Never back to this edit page — its row may have been one of the deleted members. The lock
      // stays held until the page goes.
      navigate("/payer-rules");
    } catch (error) {
      bucket.markSaving(null);
      bucket.releaseSave();
      // 409 means the bucket changed since it was loaded; the server's message says to reload,
      // and the form is left as it is so nothing the user typed is lost. 400s name the pair.
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          extractServerErrorMessage(error) ??
          "An error has occurred attempting to update these rules",
        life: 8000,
      });
    }
  };

  return (singleRowBody: Record<string, unknown>) =>
    new Promise<void>((resolve) => {
      // One save at a time — whatever the button's state: a second submit while the question is
      // open or the request is in flight does nothing.
      if (!bucket.lockSave()) {
        resolve();
        return;
      }
      const data = groupSaveData(singleRowBody, bucket.scopeDisplayName);
      const request = buildRuleGroupUpdateRequest({
        scope,
        scopeProviderId: bucket.providerId,
        groupKey: bucket.groupKey,
        ids: bucket.scopeMembers.map((member) => member.id),
        data,
        removedIds: bucket.removedIds,
        added: bucket.added,
        codeListKey: bucket.codeListKey,
        alsoUpdateCodeListFamily: family.willUpdate,
      });
      const newName =
        typeof data.groupName === "string" ? data.groupName.trim() : null;
      const lines = groupSaveConfirmLines({
        updated: bucket.activeMembers,
        addedCount: bucket.added.length,
        removedCount: bucket.removedIds.length,
        familyCount: family.willUpdate ? family.familyCount : 0,
        rename:
          newName === null
            ? null
            : {
                name: newName,
                count: bucket.activeMembers.filter(
                  (member) => (member.groupName ?? "").trim() !== newName
                ).length,
              },
      });
      let accepted = false;
      // Settles the submit as a cancel: the question was declined, or closed with X or Esc.
      const cancel = () => {
        if (accepted) return;
        bucket.releaseSave();
        resolve();
      };
      confirmDialog({
        header: "Save changes",
        icon: "pi pi-exclamation-triangle",
        message: (
          <div data-testid="group-save-confirm">
            {lines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        ),
        acceptLabel: "Update",
        rejectLabel: "Cancel",
        accept: async () => {
          accepted = true;
          await save(
            request,
            bucket.activeMembers.length + bucket.added.length
          );
          resolve();
        },
        reject: cancel,
        // PrimeReact calls onHide after accept and reject as well; only a close counts here.
        onHide: cancel,
      });
    });
}
